import PropTypes from "prop-types"
import React, { useEffect, useRef, useCallback, useState } from "react"
import axios from "axios"

//Import Icons
import FeatherIcon from "feather-icons-react";

// //Import Scrollbar
import SimpleBar from "simplebar-react"

// MetisMenu
import MetisMenu from "metismenujs"
import { Link, useLocation, useNavigate } from "react-router-dom"
import withRouter from "../Common/withRouter"

//i18n
import { withTranslation } from "react-i18next"

// API
import { API } from "../../api/urls"

//redux
import { useSelector, useDispatch } from "react-redux"
import { fetchSupportBadge, fetchMySupportBadge, clearSupportBadge } from "../../store/supportBadge/actions"

// Menü rozeti
import MenuCountBadge from "../Common/MenuCountBadge"
import { usePlanFeatures } from "../../hooks/usePlanFeatures";
import FeatureTrialModal from "../Common/FeatureTrialModal";

const ADMIN_TICKETS_PATH = "/admin/tickets";
const SUPPORT_PATH = "/support";

/** Sürüm notları — kenar çubuğundaki sürüm yazısının hedefi. */
const CHANGELOG_URL = "https://tahsilat360.com/changelog";

const SidebarContent = props => {
  const ref = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appVersion, setAppVersion] = useState("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [hasPendingRegistrations, setHasPendingRegistrations] = useState(false);
  const dispatch = useDispatch();
  const awaitingTicketCount = useSelector(state => state.supportBadge.awaitingReplyCount);
  const myAwaitingReplyCount = useSelector(state => state.supportBadge.awaitingMyReplyCount);
  const isAccountRoleUser = userRoles.some(r => r.startsWith("ROLE_ACCOUNT_"));
  const isSuperAdminUser = userRoles.some(r => r.includes("ROLE_SUPER_ADMIN"));
  const paymentWord = isAccountRoleUser ? "Ödeme" : "Tahsilat";
  const accountOrCompany = isSuperAdminUser ? "Firma" : "Cari/Bayi"

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {

        const response = await axios.get(API.auth_me);

        if (response.data.statusCode === 200) {
          setUserRoles(response.data.data.roles || []);
          setAppVersion(response.data.data.appVersion);
        }
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserInfo();
  }, [navigate]);

  useEffect(() => {
    // Yardımcı: mevcut genişliği çeşitli kaynaklardan tespit et
    const getSidebarWidth = () => {
      // 1) simplebar-placeholder'lar
      const phEls = Array.from(document.querySelectorAll(".simplebar-placeholder"));
      const phWidths = phEls
          .map((el) => {
            const w = parseFloat(getComputedStyle(el).width || "0");
            return Number.isNaN(w) ? 0 : w;
          })
          .filter((w) => w > 0);
      if (phWidths.length) return Math.max(...phWidths);

      // 2) SimpleBar wrapper (scroll element'in ebeveyni)
      const wrapper = ref.current?.getScrollElement()?.parentElement;
      if (wrapper && wrapper.clientWidth) return wrapper.clientWidth;

      // 3) Sidebar kökü (tema bazlı)
      const sidebarRoot =
          document.querySelector(".vertical-menu") ||
          document.getElementById("sidebar-menu")?.parentElement;
      if (sidebarRoot && sidebarRoot.clientWidth) return sidebarRoot.clientWidth;

      return null;
    };

    const THRESHOLD = 100; // 69px kapalı, 249px açık → 100 altını kapalı say
    const update = () => {
      // Eğer body 'vertical-collapsed' kullanıyorsa onu önceliklendirelim
      const bodyCollapsed = document.body.classList.contains("vertical-collapsed");
      if (bodyCollapsed !== isSidebarCollapsed) {
        setIsSidebarCollapsed(bodyCollapsed);
        return;
      }
      const w = getSidebarWidth();
      if (w == null) return; // ölçüm yoksa dokunma (varsayılan açık kalsın)
      setIsSidebarCollapsed(w < THRESHOLD);
    };

    // İlk ölçüm → açık kalsın (flash yok)
    setIsSidebarCollapsed(false);
    update();

    // ResizeObserver: placeholder'lar + wrapper + sidebar root
    const ro = new ResizeObserver(update);
    const targets = [
      ...document.querySelectorAll(".simplebar-placeholder"),
      ref.current?.getScrollElement()?.parentElement,
      document.querySelector(".vertical-menu"),
    ].filter(Boolean);
    targets.forEach((el) => ro.observe(el));

    // Body class değişimini izle (tema collapse toggle'ı genelde burada)
    const mo = new MutationObserver(update);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    // Ek sinyaller
    window.addEventListener("resize", update);
    window.addEventListener("transitionend", update);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("transitionend", update);
    };
  }, []);

  // Bekleyen (PENDING) kayıt talebi var mı? Süper admin için sol menüde turuncu nokta gösterilir.
  // Sayfa değiştikçe yeniden kontrol edilir (talep onaylanınca/reddedilince nokta güncellensin).
  useEffect(() => {
    if (!isSuperAdminUser) {
      setHasPendingRegistrations(false);
      return;
    }
    let cancelled = false;
    const checkPendingRegistrations = async () => {
      try {
        const res = await axios.get(API.admin_registrations_list, {
          params: { status: "PENDING", page: 0, size: 1 }
        });
        if (!cancelled && res.data?.statusCode === 200) {
          setHasPendingRegistrations((res.data.data?.totalElements || 0) > 0);
        }
      } catch (error) {
        // Sessiz geç: menü rozeti kritik değil
      }
    };
    checkPendingRegistrations();
    return () => { cancelled = true; };
  }, [isSuperAdminUser, location.pathname]);

  // Paketteki açık özellikler (süren denemeler dâhil). Liste yüklenene kadar
  // has() true döner; menü sebepsiz uyarı penceresine bağlanmasın diye
  // (asıl kısıt zaten backend'de).
  const { has: hasFeature, loaded: featuresLoaded, trialFor, trialDays, startTrial } = usePlanFeatures();
  const canUseSupport = featuresLoaded && hasFeature("SUPPORT_TICKETS");

  // Personel yanıtı bekleyen destek talebi sayısı — süper admin menüsünde rozet olarak gösterilir.
  // Sayı backend'de (DB count) hesaplanır ve store'da tutulur; talep yanıtlandığında ilgili sayfa
  // fetchSupportBadge() dispatch ederek rozeti anında günceller.
  useEffect(() => {
    // Süper admin iki rozeti birden görür: personel kuyruğu (Destek Yönetimi) ve kendi
    // taleplerim (kendi açtıkları + adına açtıkları). Diğer roller yalnız ikincisini.
    if (isSuperAdminUser) dispatch(fetchSupportBadge());
    // Paketinde destek talebi olmayan firmada (ör. deneme süresi) ve onun carilerinde sorulmaz:
    // arka plandaki rozet isteğinin 403'ü her sayfa değişiminde "paketinize dahil değil"
    // penceresini açıyordu.
    if (canUseSupport) dispatch(fetchMySupportBadge());
  }, [dispatch, isSuperAdminUser, canUseSupport, location.pathname]);

  // --- Daraltılmış menüde yana açılan alt menü (flyout) yönetimi ---
  // Eskiden flyout yalnızca CSS :hover ile açılıyordu. İmleç ana ikondan alt
  // menüye kayarken aradaki 2px'lik boşlukta hover kaybolduğu için menü aniden
  // kapanıyordu. Artık açık menü state'te tutulur:
  //   - üzerine gelinince açılır, ayrılınca 300ms gecikmeyle kapanır (imlecin
  //     boşluktan geçmesine tolerans tanır)
  //   - ana sekmeye tıklanınca sabitlenir (pin) ve başka bir yere tıklanana,
  //     Esc'e basılana veya sayfa değişene kadar açık kalır
  const [openFlyout, setOpenFlyout] = useState(null);
  const flyoutPinnedRef = useRef(false);
  const flyoutTimerRef = useRef(null);

  const isCondensed = () => document.body.getAttribute("data-sidebar-size") === "sm";

  const clearFlyoutTimer = () => {
    if (flyoutTimerRef.current) {
      clearTimeout(flyoutTimerRef.current);
      flyoutTimerRef.current = null;
    }
  };

  const closeFlyout = useCallback(() => {
    clearFlyoutTimer();
    flyoutPinnedRef.current = false;
    setOpenFlyout(null);
  }, []);

  const handleFlyoutEnter = (index) => {
    if (!isCondensed()) return;
    clearFlyoutTimer();
    // Başka bir menüye geçildiyse önceki sabitleme düşer.
    if (openFlyout !== index) flyoutPinnedRef.current = false;
    setOpenFlyout(index);
  };

  const handleFlyoutLeave = () => {
    if (!isCondensed() || flyoutPinnedRef.current) return;
    clearFlyoutTimer();
    flyoutTimerRef.current = setTimeout(() => setOpenFlyout(null), 300);
  };

  const handleParentClick = (e, index) => {
    // Geniş menüde MetisMenu akordeonu çalışmaya devam etsin.
    if (!isCondensed()) return;
    e.preventDefault();
    clearFlyoutTimer();
    if (openFlyout === index && flyoutPinnedRef.current) {
      flyoutPinnedRef.current = false;
      setOpenFlyout(null);
    } else {
      flyoutPinnedRef.current = true;
      setOpenFlyout(index);
    }
  };

  // Dışarı tıklama, Esc ve menünün genişlemesi flyout'u kapatır.
  useEffect(() => {
    const onMouseDown = (e) => {
      const target = e.target;
      if (!(target instanceof Element) || !target.closest(".vertical-menu")) {
        closeFlyout();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeFlyout();
    };
    const onSidebarSizeChange = () => {
      if (!isCondensed()) closeFlyout();
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    const mo = new MutationObserver(onSidebarSizeChange);
    mo.observe(document.body, { attributes: true, attributeFilter: ["data-sidebar-size"] });

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      mo.disconnect();
      clearFlyoutTimer();
    };
  }, [closeFlyout]);

  // Alt menüden bir sayfaya gidilince flyout kapansın.
  useEffect(() => {
    closeFlyout();
  }, [location.pathname, location.search, closeFlyout]);

  // Rol kontrolü fonksiyonu ve menuItems (Aynı)

  // Paket dışı bir menüye tıklandığında açılan pencerenin hedefi.
  // { feature: "POS_ANALYTICS", label: "POS Rapor", path: "/admin/pos-raporu" }
  const [trialPrompt, setTrialPrompt] = useState(null);

  // MetisMenu örneği; menü değişince dispose edilip yeniden kurulur.
  const metisMenuRef = useRef(null);

  const hasRole = (requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return requiredRoles.some(role => userRoles.includes(role));
  };

  const dashboardItem = { path: "/dashboard", icon: "home", label: "Kontrol Paneli", roles: [], tourId: "nav-dashboard" };
  const usersItem = { path: "/users", icon: "users", label: "Kullanıcılar", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN"] };
  const companiesItem = { path: "/companies", icon: "grid", label: "Firmalar", roles: ["ROLE_SUPER_ADMIN"] };
  // POS Rapor: tek giriş noktası. Sağlayıcı → firma → cari kırılımı bu ekranın
  // içinden (POS Entegrasyon Detayları tablosuna tıklanarak) gezilir.
  const posReportItem = { path: "/admin/pos-raporu", icon: "bar-chart-2", label: "POS Rapor", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN"], feature: "POS_ANALYTICS" };
  const registrationRequestsItem = { path: "/registration-requests", icon: "user-check", label: "Kayıt Talepleri", roles: ["ROLE_SUPER_ADMIN"] };
  const accountsItem = { path: "/accounts", icon: "briefcase", label: "Cariler/Bayiler", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON",], tourId: "nav-accounts" };
  const paymentMenuItem = {
      icon: "credit-card",
      label: paymentWord, // <-- Ödeme/Tahsilat
      tourId: "nav-tahsilat",
      roles: [
        "ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER",
        "ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",
      ],
      hasArrow: true,
      subItems: [
        {
          path: "/payment?type=normal",
          label: `Anlık ${paymentWord}`, // <-- Anlık Ödeme/Tahsilat
          roles: [
            "ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER",
            "ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",
          ],
        },
        {
          path: "/payment?type=account",
          label: `${accountOrCompany} ile ${paymentWord}`, // <-- Cari/Bayi ile Ödeme/Tahsilat
          roles: [
            "ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER",
          ],
        },
        {
          path: "/payment?type=invoice",
          label: `Fatura Bilgisi ile ${paymentWord}`, // <-- Fatura Bilgisi ile Ödeme/Tahsilat
          roles: [
            "ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER",
            "ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",
          ],
        },
      ],
    };
  const paymentLinksItem = { path: "/payment-links", icon: "link", label: `Link ile ${paymentWord}`, feature: "PAYMENT_LINK", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER","ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",], tourId: "nav-payment-links" };
  const paymentHistoryItem = { path: "/payment-history", icon: "clock", label: `${paymentWord} Geçmişi`, roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER","ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",], tourId: "nav-payment-history" };
  // İade Geçmişi finansal denetim ekranıdır: iade YAPMA yetkisiyle aynı üç role açıktır,
  // Ödeme Geçmişi'nden daha dardır (satış/kasa ve cari rolleri göremez).
  //
  // ŞU AN MENÜDE GİZLİ: sayfaya Ödeme Geçmişi ekranındaki "İade Geçmişi" butonundan
  // gidiliyor. Tanım ve /refund-history route'u duruyor — menüde göstermek için
  // aşağıdaki iki dizide yorumu kaldırmak yeterli.
  // eslint-disable-next-line no-unused-vars
  const refundHistoryItem = { path: "/refund-history", icon: "corner-up-left", label: "İade Geçmişi", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT",], tourId: "nav-refund-history" };
  const invoiceTrackingItem = { path: "/invoice-tracking", icon: "file", label: isAccountRoleUser ? "Borç Takip" : "Vade Takip", feature: "DEBT_REMINDER", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER","ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",], tourId: "nav-invoice-tracking" };
  const scheduledReportsItem = { path: "/schedule-reports", icon: "file-text", label: "Zamanlanmış Raporlar", feature: "SCHEDULED_REPORTS", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER","ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",] };
  const subscriptionItem = // Abonelik (Company Admin)
    {
      icon: "package",
      label: "Abonelik",
      roles: ["ROLE_COMPANY_ADMIN"],
      hasArrow: true,
      subItems: [
        { path: "/subscription/details", label: "Aboneliğim", roles: ["ROLE_COMPANY_ADMIN"] },
        { path: "/subscription/plans", label: "Paketler", roles: ["ROLE_COMPANY_ADMIN"] },
        { path: "/subscription/history", label: "Abonelik Geçmişi", roles: ["ROLE_COMPANY_ADMIN"] },
      ],
    };
  const subscriptionAdminItem = // Abonelik Yonetimi (Super Admin)
    {
      icon: "layers",
      label: "Abonelik Yönetimi",
      roles: ["ROLE_SUPER_ADMIN"],
      hasArrow: true,
      subItems: [
        { path: "/admin/subscription-dashboard", label: "Genel Bakış", roles: ["ROLE_SUPER_ADMIN"] },
        { path: "/admin/subscription-plans", label: "Paketler", roles: ["ROLE_SUPER_ADMIN"] },
        { path: "/admin/coupon-codes", label: "Kupon Kodları", roles: ["ROLE_SUPER_ADMIN"] },
        { path: "/admin/price-overrides", label: "Fiyat Müdahaleleri", roles: ["ROLE_SUPER_ADMIN"] },
      ],
    };
  const settingsItem = { path: "/settings", icon: "settings", label: "Ayarlar", roles: ["ROLE_SUPER_ADMIN","ROLE_COMPANY_ADMIN","ROLE_COMPANY_ACCOUNTANT","ROLE_COMPANY_SALESPERSON","ROLE_COMPANY_CASHIER","ROLE_ACCOUNT_ACCOUNTANT","ROLE_ACCOUNT_ADMIN",], tourId: "nav-settings" };

  // Destek: süper adminde tek üst menü (Tahsilat gibi açılır/kapanır), diğer rollerde tek satır.
  const supportMenuItem = isSuperAdminUser
      ? {
        icon: "headphones",
        label: "Destek",
        roles: ["ROLE_SUPER_ADMIN"],
        hasArrow: true,
        subItems: [
          { path: ADMIN_TICKETS_PATH, label: "Destek Yönetimi", roles: ["ROLE_SUPER_ADMIN"] },
          { path: SUPPORT_PATH, label: "Taleplerim", roles: ["ROLE_SUPER_ADMIN"] },
        ],
      }
      // Cari (bayi) rolleri, bağlı oldukları firmanın paketinde destek talebi varsa görür.
      // Yoksa menü hiç çıkmaz: cari paket yükseltemez, deneme/yükseltme penceresi ona anlamsız.
      // Asıl sınır backend'de (TicketController + PlanFeatureInterceptor), bu yalnız menü gizleme.
      : {
        path: SUPPORT_PATH, icon: "headphones", label: "Destek Taleplerim",
        roles: [
          "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT", "ROLE_COMPANY_SALESPERSON", "ROLE_COMPANY_CASHIER",
          ...(canUseSupport ? ["ROLE_ACCOUNT_ADMIN", "ROLE_ACCOUNT_ACCOUNTANT"] : []),
        ],
        // Paket dışıysa menü yine görünür; tıklanınca deneme/yükseltme penceresi
        // açılır ve istek hiç gitmez. Süper adminde bu alan yok — o zaten muaf.
        feature: "SUPPORT_TICKETS",
      };

  // Menü sırası role göre değişir; görünürlük kararı aşağıdaki rol filtresinde verilir.
  const menuItems = isSuperAdminUser
      ? [
        dashboardItem,
        usersItem,
        companiesItem,
        posReportItem,
        registrationRequestsItem,
        accountsItem,
        paymentMenuItem,
        paymentLinksItem,
        paymentHistoryItem,
        // refundHistoryItem,   // menüde gizli — bkz. tanımdaki not
        invoiceTrackingItem,
        scheduledReportsItem,
        subscriptionItem,
        subscriptionAdminItem,
        supportMenuItem,
        settingsItem,
      ]
      : [
        dashboardItem,
        paymentMenuItem,
        paymentLinksItem,
        paymentHistoryItem,
        // refundHistoryItem,   // menüde gizli — bkz. tanımdaki not
        posReportItem,
        usersItem,
        accountsItem,
        invoiceTrackingItem,
        scheduledReportsItem,
        subscriptionItem,
        supportMenuItem,
        settingsItem,
      ];

  /**
   * Paketinde olmayan menü. Gizlenmez ve KİLİTLENMEZ — normal görünür, tıklanınca
   * sayfaya gitmek yerine deneme/yükseltme penceresi açılır. Amaç kullanıcının
   * özelliği görüp deneyebilmesi; erişimi zaten backend engelliyor (403).
   */
  const isOutOfPlan = (item) => !!item.feature && !hasFeature(item.feature);

  /** Paket dışı menüye tıklama: navigasyon durur, pencere açılır. */
  const handleOutOfPlanClick = (e, item) => {
    e.preventDefault();
    // MetisMenu tıklanan öğeyi kendi işleyicisinde aktif işaretliyor. Normal bir
    // menüde bu görünmez çünkü hemen ardından sayfa değişir; burada navigasyon
    // olmadığı için öğe "o sayfadaymışız gibi" seçili kalıyordu.
    e.stopPropagation();
    e.currentTarget.blur();
    closeFlyout();
    setTrialPrompt({ feature: item.feature, label: item.label, path: item.path });
    // Aktiflik sınıflarını gerçek konuma göre yeniden kur (kütüphane sınıfı
    // senkron eklediği için bir tur sonraya bırakılır).
    setTimeout(() => activeMenu(), 0);
  };

  /**
   * Deneme başlar başlamaz özellik açılır (backend paket kümesine ekler), bu yüzden
   * kullanıcı tıkladığı sayfaya yönlendirilir — pencereyi kapatıp menüye geri
   * dönmek zorunda kalmasın.
   */
  const handleStartTrial = async () => {
    const target = trialPrompt;
    await startTrial(target.feature);
    setTrialPrompt(null);
    if (target.path) navigate(target.path);
  };

  const visibleMenuItems = menuItems.filter(item => {
    // ... (Aynı)
    if (!hasRole(item.roles)) return false;

    // Paket özellik kısıtı menüyü GİZLEMEZ ve kilitlemez; tıklanınca deneme /
    // yükseltme penceresi açılır (bkz. isOutOfPlan, handleOutOfPlanClick).

    if (item.subItems) {
      const visibleSubItems = item.subItems.filter(subItem => hasRole(subItem.roles));
      return visibleSubItems.length > 0;
    }

    return true;
  });

  /** Menü listesinin kimliği — içerik değişince MetisMenu yeniden kurulsun diye. */
  const menuSignature = visibleMenuItems.map(i => i.path || i.label).join("|");

  const activateParentDropdown = useCallback((item) => {
    // ... (Aynı)
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];

    if (parent2El && parent2El.id !== "side-menu") {
      parent2El.classList.add("mm-show");
    }

    if (parent) {
      parent.classList.add("mm-active");
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add("mm-show"); // ul tag

        const parent3 = parent2.parentElement; // li tag

        if (parent3) {
          parent3.classList.add("mm-active"); // li
          parent3.childNodes[0].classList.add("mm-active"); //a
          const parent4 = parent3.parentElement; // ul
          if (parent4) {
            parent4.classList.add("mm-show"); // ul
            const parent5 = parent4.parentElement;
            if (parent5) {
              parent5.classList.add("mm-show"); // li
              parent5.childNodes[0].classList.add("mm-active"); // a tag
            }
          }
        }
      }
      scrollElement(item);
      return false;
    }
    scrollElement(item);
    return false;
  }, []);

  const removeActivation = (items) => {
    // ... (Aynı)
    for (var i = 0; i < items.length; ++i) {
      var item = items[i];
      const parent = items[i].parentElement;

      if (item && item.classList.contains("active")) {
        item.classList.remove("active");
      }
      if (parent) {
        const parent2El =
            parent.childNodes && parent.childNodes.lenght && parent.childNodes[1]
                ? parent.childNodes[1]
                : null;
        if (parent2El && parent2El.id !== "side-menu") {
          parent2El.classList.remove("mm-show");
        }

        parent.classList.remove("mm-active");
        const parent2 = parent.parentElement;

        if (parent2) {
          parent2.classList.remove("mm-show");

          const parent3 = parent2.parentElement;
          if (parent3) {
            parent3.classList.remove("mm-active"); // li
            parent3.childNodes[0].classList.remove("mm-active");

            const parent4 = parent3.parentElement; // ul
            if (parent4) {
              parent4.classList.remove("mm-show"); // ul
              const parent5 = parent4.parentElement;
              if (parent5) {
                parent5.classList.remove("mm-show"); // li
                parent5.childNodes[0].classList.remove("mm-active"); // a tag
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    // ... (Aynı)
    const pathName = location.pathname;
    const searchParams = location.search;
    const fullPath = pathName + searchParams;

    let matchingMenuItem = null;
    const ul = document.getElementById("side-menu");
    const items = ul ? ul.getElementsByTagName("a") : [];
    removeActivation(items);

    // Önce tam eşleşme ara (pathname + search)
    for (let i = 0; i < items.length; ++i) {
      const itemHref = items[i].getAttribute('href');
      if (!itemHref || itemHref === "#" || items[i].classList.contains("has-arrow")) {
        continue;
      }
      if (itemHref && itemHref === fullPath) {
        matchingMenuItem = items[i];
        break;
      }
    }

    // Tam eşleşme bulunamazsa sadece pathname'e bak
    if (!matchingMenuItem) {
      for (let i = 0; i < items.length; ++i) {
        const itemHref = items[i].getAttribute('href');
        if (!itemHref || itemHref === "#" || items[i].classList.contains("has-arrow")) {
          continue;
        }
        if (pathName === items[i].pathname) {
          matchingMenuItem = items[i];
          break;
        }
      }
    }

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [location.pathname, location.search, activateParentDropdown]);

  useEffect(() => {
    if (ref.current) {
      ref.current.recalculate();
    }
  }, [visibleMenuItems]);

  // MetisMenu, kurulduğu andaki DOM'a göre tetikleyicileri bağlar: alt menüsü olan
  // her <a>'ya bir click dinleyicisi takar. Menü listesi sonradan değişirse (paket
  // özellikleri asenkron geldiği için oluyor) bu dinleyiciler alt menüsü kalmamış
  // bağlantılarda asılı kalıyor ve tıklanınca kütüphane `ul.closest` üzerinde
  // patlıyordu. Bu yüzden liste her değiştiğinde örnek atılıp yeniden kuruluyor.
  useEffect(() => {
    if (loading) return;
    metisMenuRef.current?.dispose();
    metisMenuRef.current = new MetisMenu("#side-menu");
    return () => {
      metisMenuRef.current?.dispose();
      metisMenuRef.current = null;
    };
  }, [loading, menuSignature]);

  // Yeniden kurulum mm-active sınıflarını sıfırladığı için aktif menü tekrar işaretlenir.
  useEffect(() => {
    if (!loading) activeMenu();
  }, [activeMenu, loading, menuSignature]);

  // Sayfa değişiminde başa dön. YALNIZCA pathname'e bağlı: activeMenu location.search'ü
  // de dinlediği için bu, onunla aynı efektte olduğunda her query parametresi
  // değişiminde tetikleniyordu — tabloda satır seçmek (?detail=<id>) ya da filtre
  // uygulamak sayfayı yukarı atıyordu.
  useEffect(() => {
    if (!loading) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, loading]);

  // activeMenu() ve MetisMenu'nun müdahalesinden sonra çalışır.
  // Top-level (submenu olmayan) itemların active/mm-active class'larını kesin olarak düzeltir.
  useEffect(() => {
    if (loading) return;
    const sideMenu = document.getElementById('side-menu');
    if (!sideMenu) return;
    const topLinks = sideMenu.querySelectorAll(':scope > li > a:not(.has-arrow)');
    topLinks.forEach(link => {
      const href = link.getAttribute('href');
      const li = link.parentElement;
      if (!li) return;
      const isActive = href === location.pathname;
      link.classList.toggle('active', isActive);
      li.classList.toggle('mm-active', isActive);
    });
  }, [location.pathname, loading]);

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop;
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  // Loading durumu (Aynı)
  if (loading) {
    return (
        <React.Fragment>
          <SimpleBar style={{ maxHeight: "100%" }} ref={ref}>
            <div id="sidebar-menu">
              <ul className="metismenu list-unstyled" id="side-menu">
                <li className="menu-title">Yükleniyor...</li>
              </ul>
            </div>
          </SimpleBar>
        </React.Fragment>
    );
  }

  return (
      <React.Fragment>
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <SimpleBar style={{ maxHeight: "100%", flexGrow: 1, minHeight: 0 }} ref={ref}>
            <div id="sidebar-menu">
              <ul className="metismenu list-unstyled" id="side-menu">
                <li className="menu-title">{props.t("Menü")} </li>

                {visibleMenuItems.map((item, index) => {
                  const currentFullPath = location.pathname + location.search;
                  const isSubmenuItemActive = !!item.subItems?.some(
                      subItem => currentFullPath === subItem.path
                  );
                  const isDirectItemActive = !item.subItems && location.pathname === item.path;
                  const outOfPlan = isOutOfPlan(item);

                  return (
                    <li
                      key={item.path || item.label}
                      className={[
                        isSubmenuItemActive || isDirectItemActive ? 'mm-active' : '',
                        item.subItems && openFlyout === index ? 'flyout-open' : '',
                      ].filter(Boolean).join(' ')}
                      onMouseEnter={item.subItems ? () => handleFlyoutEnter(index) : undefined}
                      onMouseLeave={item.subItems ? handleFlyoutLeave : undefined}
                    >
                      {item.subItems ? (
                          <>
                            <Link
                              to="#"
                              className={`has-arrow ${isSubmenuItemActive ? "mm-active" : ""}`}
                              data-tour={item.tourId || undefined}
                              onClick={(e) => handleParentClick(e, index)}
                            >
                              <FeatherIcon icon={item.icon} />
                              <span>{props.t(item.label)}</span>
                              {item.subItems.some(sub => sub.path === ADMIN_TICKETS_PATH) && (
                                <MenuCountBadge
                                  count={awaitingTicketCount}
                                  title="Yanıt bekleyen destek talebi"
                                />
                              )}
                            </Link>
                            <ul className="sub-menu">
                              {item.subItems
                                  .filter(subItem => hasRole(subItem.roles))
                                  .map((subItem, subIndex) => (
                                      <li key={subItem.path || subItem.label}>
                                        <Link
                                            to={subItem.path}
                                            className={currentFullPath === subItem.path ? 'active' : ''}
                                            onClick={isOutOfPlan(subItem) ? (e) => handleOutOfPlanClick(e, subItem) : undefined}
                                        >
                                          {props.t(subItem.label)}
                                          {subItem.path === ADMIN_TICKETS_PATH && (
                                            <MenuCountBadge
                                              count={awaitingTicketCount}
                                              title="Yanıt bekleyen destek talebi"
                                              dot
                                            />
                                          )}
                                          {/* Adına talep açtığım kullanıcı yanıt yazdığında da yanar. */}
                                          {subItem.path === SUPPORT_PATH && (
                                            <MenuCountBadge
                                              count={myAwaitingReplyCount}
                                              title="Talebinize yanıt geldi"
                                              dot
                                            />
                                          )}
                                        </Link>
                                      </li>
                                  ))}
                            </ul>
                          </>
                      ) : (
                          <Link
                            to={item.path}
                            className={isDirectItemActive ? 'active' : ''}
                            data-tour={item.tourId || undefined}
                            onClick={outOfPlan ? (e) => handleOutOfPlanClick(e, item) : undefined}
                          >
                            <FeatherIcon icon={item.icon} />
                            <span>{props.t(item.label)}</span>
                            {item.path === "/registration-requests" && hasPendingRegistrations && (
                              <span
                                title="Yeni kayıt talebi var"
                                style={{
                                  display: 'inline-block',
                                  width: '9px',
                                  height: '9px',
                                  marginLeft: '8px',
                                  borderRadius: '50%',
                                  backgroundColor: '#E56A22',
                                  boxShadow: '0 0 0 2px rgba(229,106,34,0.35)',
                                  verticalAlign: 'middle',
                                }}
                              />
                            )}
                            {item.path === ADMIN_TICKETS_PATH && (
                              <MenuCountBadge
                                count={awaitingTicketCount}
                                title="Yanıt bekleyen destek talebi"
                              />
                            )}
                            {item.path === SUPPORT_PATH && (
                              <MenuCountBadge
                                count={myAwaitingReplyCount}
                                title="Talebinize yanıt geldi"
                                dot
                              />
                            )}
                          </Link>
                      )}
                    </li>
                  );
                })}

              </ul>
            </div>
          </SimpleBar>

          <div className="text-center p-3 text-muted">
            {/* Sürüm yazısı changelog'a götürür. Dış siteye açıldığı için yeni sekme +
                noopener: aksi halde açılan sayfa window.opener üzerinden panele erişebilir. */}
            <a
              href={CHANGELOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted text-decoration-none app-version-link"
              title="Sürüm notlarını görüntüle"
            >
              <small>Versiyon {appVersion || '1.0.0'}</small>
              {/* Kutudan çıkan ok: bağlantının paneli terk ettiğini tıklamadan önce
                  söyler. aria-hidden — anlamı zaten title metninde. */}
              <FeatherIcon
                icon="external-link"
                size={14}
                className="app-version-link__icon"
                aria-hidden="true"
              />
            </a>
          </div>

          {/* Paket dışı menüye tıklandığında: ücretsiz deneme ya da paket yükseltme. */}
          {trialPrompt && (
            <FeatureTrialModal
              isOpen={true}
              onClose={() => setTrialPrompt(null)}
              feature={{ code: trialPrompt.feature, label: props.t(trialPrompt.label) }}
              trial={trialFor(trialPrompt.feature)}
              trialDays={trialDays}
              onStartTrial={handleStartTrial}
              onUpgrade={() => {
                setTrialPrompt(null);
                navigate("/subscription/plans");
              }}
            />
          )}
        </div>
      </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))
